import { AxiosStatic } from 'axios';

import { usePhoneCallStore } from 'src/stores/phoneCall';

import { AbstractPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/AbstractPhoneCallGatewayWithHangupFunctionalityStrategy';
import { DirectPhoneCallGatewayStrategy } from 'src/components/phoneCall/phoneCallGateway/DirectPhoneCallGatewayStrategy';
import { phoneCallProviderRequestConfig } from 'src/components/phoneCall/phoneCallGateway/phoneCallProviderRequestConfig';
import {
	SipGateResponse,
	SipgateCallerAndDeviceId,
} from 'src/components/phoneCall/phoneCallGateway/sipgate/InterfacesAndTypes';

import { OutgoingCallConfigurationForTheUser } from 'src/models/phoneCall';

import { CommandBus } from 'src/util/command/CommandBus';
import { encodeBase64 } from 'src/util/common';

type PhoneCallStore = ReturnType<typeof usePhoneCallStore>;

export class SipgateDirectPhoneCallGatewayStrategy
	extends AbstractPhoneCallGatewayWithHangupFunctionalityStrategy
	implements DirectPhoneCallGatewayStrategy
{
	private static SIPGATE_INIT_CALL_API_URL = 'https://api.sipgate.com/v2/sessions/calls';

	private static SIPGATE_END_CALL_API_URL = 'https://api.sipgate.com/v2/calls/';

	private readonly axios: AxiosStatic;
	private outGoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser | null = null;

	constructor(
		axios: AxiosStatic,
		phoneCallStore: PhoneCallStore,
		commandBus: CommandBus,
		authenticatedUserId: number,
		outGoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser
	) {
		super(phoneCallStore, commandBus, authenticatedUserId);
		this.axios = axios;
		this.outGoingCallConfigurationForTheUser = outGoingCallConfigurationForTheUser;
	}

	public async makeCall(callerName: string, number: string): Promise<null | string> {
		const sipgateCallerAndDeviceId: SipgateCallerAndDeviceId | null = this.sipgateCallerAndDeviceId();

		if (sipgateCallerAndDeviceId !== null) {
			const result = await this.axios.post<SipGateResponse>(
				SipgateDirectPhoneCallGatewayStrategy.SIPGATE_INIT_CALL_API_URL,
				{
					callee: number,
					caller: sipgateCallerAndDeviceId.caller,
					deviceId: sipgateCallerAndDeviceId.deviceId,
				},
				phoneCallProviderRequestConfig({
					headers: this.sipgateRequestHeader(),
				})
			);

			this.setActiveCallId(result.data.sessionId);
			return await this.createPhoneCall(callerName, number);
		}

		return null;
	}

	public endCall(): void {
		if (this.activeCallId() !== null) {
			void this.axios.delete(
				`${SipgateDirectPhoneCallGatewayStrategy.SIPGATE_END_CALL_API_URL}${this.activeCallId() as string}`,
				phoneCallProviderRequestConfig({
					headers: this.sipgateRequestHeader(),
				})
			);
		}

		this.clearActiveCall();
	}

	private sipgateRequestHeader() {
		const sipgateIOAuthToken = encodeBase64(
			`${this.outGoingCallConfigurationForTheUser?.tokenId as string}:${this.outGoingCallConfigurationForTheUser?.token as string}`
		);

		return {
			Accept: 'application/json',
			Authorization: `Basic ${sipgateIOAuthToken}`,
			'Content-Type': 'application/json',
			'X-Platform': false,
		};
	}

	private sipgateCallerAndDeviceId(): SipgateCallerAndDeviceId | null {
		const tapiTerminal = this.outGoingCallConfigurationForTheUser?.terminal.split(':');

		if (tapiTerminal !== undefined) {
			return {
				caller: tapiTerminal[1],
				deviceId: tapiTerminal[0],
			};
		}

		return null;
	}
}
